import { Fragment, useEffect, useState, type FormEvent, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { FlaskConical } from 'lucide-react'

import { apiSend, errorMessage, isApiError } from '@/lib/api'
import { sessionKeys, useAuthConfig, useMe, type AuthConfig, type Me } from '@/lib/session'
import { getSessionLifecycle, sessionIdentity, useSessionBlocked } from '@/lib/session-cache'
import { ErrorNotice } from '@/components/kit/page'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'

/**
 * Renders the app only for a known caller. Local mode never asks to sign in (every request is the local
 * admin); the shared-password and Google modes show the sign-in screen on 401.
 */
export function AuthGate({ children }: { children: (session: { me: Me; config: AuthConfig }) => ReactNode }) {
  const queryClient = useQueryClient()
  const blocked = useSessionBlocked()
  const config = useAuthConfig()
  const me = useMe()

  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === 'hidden') return
      void queryClient.invalidateQueries({ queryKey: sessionKeys.config, exact: true })
      void queryClient.invalidateQueries({ queryKey: sessionKeys.me, exact: true })
    }
    window.addEventListener('focus', refresh)
    document.addEventListener('visibilitychange', refresh)
    return () => {
      window.removeEventListener('focus', refresh)
      document.removeEventListener('visibilitychange', refresh)
    }
  }, [queryClient])

  if (config.isPending || me.isPending) {
    return (
      <div className="flex min-h-svh items-center justify-center gap-2 text-sm text-muted-foreground">
        <Spinner /> Loading Testbench…
      </div>
    )
  }

  if (config.isError) {
    return (
      <Centered>
        <ErrorNotice error={config.error} />
      </Centered>
    )
  }

  if (me.isError) {
    if (isApiError(me.error) && me.error.status === 401) {
      return <SignInScreen config={config.data} />
    }
    return (
      <Centered>
        <ErrorNotice error={me.error} />
      </Centered>
    )
  }

  if (blocked) {
    return <Centered><Spinner /> Checking your session…</Centered>
  }

  return <Fragment key={sessionIdentity(me.data)}>{children({ me: me.data, config: config.data })}</Fragment>
}

function Centered({ children }: { children: ReactNode }) {
  return <div className="flex min-h-svh items-center justify-center p-6"><div className="w-full max-w-md">{children}</div></div>
}

function SignInScreen({ config }: { config: AuthConfig }) {
  return (
    <Centered>
      <Card>
        <CardHeader>
          <div className="mb-2 flex size-9 items-center justify-center rounded-md bg-sidebar-primary text-sidebar-primary-foreground">
            <FlaskConical className="size-4" />
          </div>
          <CardTitle className="text-lg">Sign in to Wargard Testbench</CardTitle>
          <CardDescription>
            {config.mode === 'google'
              ? 'Use the Google account an admin invited.'
              : 'Enter the team password. Ask the QA Lead if you do not have it.'}
          </CardDescription>
        </CardHeader>
        <CardContent>{config.mode === 'google' ? <GoogleSignIn /> : <SharedPasswordSignIn />}</CardContent>
      </Card>
    </Centered>
  )
}

function SharedPasswordSignIn() {
  const queryClient = useQueryClient()
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await apiSend('/auth/signin', 'POST', { password })
      await getSessionLifecycle(queryClient).recheck()
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="flex flex-col gap-4" onSubmit={submit}>
      <div className="flex flex-col gap-2">
        <Label htmlFor="password">Team password</Label>
        <Input id="password" type="password" autoComplete="current-password" required autoFocus value={password} onChange={(e) => setPassword(e.target.value)} />
      </div>
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      <Button type="submit" disabled={busy}>
        {busy && <Spinner />} Sign in
      </Button>
    </form>
  )
}

function GoogleSignIn() {
  const refused = new URLSearchParams(window.location.search).get('error') === 'google'
  return (
    <div className="flex flex-col gap-4">
      {refused && (
        <p role="alert" className="text-sm text-destructive">
          Google sign-in was refused: this Google account is not invited to Testbench, or it is disabled. Ask an admin to add your exact
          address.
        </p>
      )}
      <Button type="button" onClick={() => window.location.assign(`/auth/google?returnUrl=${encodeURIComponent(window.location.pathname)}`)}>
        Sign in with Google
      </Button>
    </div>
  )
}
